import React, { useCallback, useEffect, useState } from "react";
import { Card, BlockStack, InlineStack, Text } from "@shopify/polaris";
import { useSelector } from "react-redux";
import { GetApiCall, ApiCall } from "../../helper/axios";

const STAR_PATH =
    "M12 2.5l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.77l-5.8 3.05 1.1-6.46-4.69-4.58 6.49-.94L12 2.5z";

/*
 * Inline review prompt shown at the top of the Analytics tab, built with Polaris.
 * Stars are interactive: 5 stars -> straight to the App Store (and the banner
 * hides for good); 1-4 stars -> opens the feedback modal. Visibility is driven by
 * the server (bannerShow): hidden permanently after a 5-star review, re-shown 30
 * days after low-rating feedback.
 */
function ReviewBanner({ onOpenFeedback, refreshSignal = 0 }) {
    const { store_data } = useSelector((store) => store.commonData);
    const token = store_data?.token;

    const [show, setShow] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [hover, setHover] = useState(0);
    const [appStoreUrl, setAppStoreUrl] = useState("https://apps.shopify.com/currency-converter#modal-show=WriteReviewModal");

    const fetchEligibility = useCallback(async () => {
        if (!token) return;
        try {
            const res = await GetApiCall("GET", "/review/eligibility", { authentication: token });
            const d = res?.data?.data;
            if (res?.data?.statusCode === 200 && d) {
                if (d.appStoreUrl) setAppStoreUrl(d.appStoreUrl);
                setShow(!!d.bannerShow);
            }
        } catch (e) {
            /* never block the dashboard on the review check */
        }
    }, [token]);

    useEffect(() => {
        fetchEligibility();
    }, [fetchEligibility, refreshSignal]);

    const sendEvent = (action, extra = {}) =>
        ApiCall("POST", "/review/event", { action, ...extra }, { authentication: token }).catch(() => {});

    const onStar = (n) => {
        sendEvent("rated", { rating: n });
        if (n === 5) {
            sendEvent("redirected");
            try {
                window.open(appStoreUrl, "_blank", "noopener,noreferrer");
            } catch (e) {
                /* ignore */
            }
            setHidden(true); // hide immediately; server keeps it hidden afterwards
        } else if (onOpenFeedback) {
            onOpenFeedback(n); // open the feedback modal pre-filled with this rating
        }
    };

    if (hidden || !show) return null;

    const active = hover;
    return (
        <div style={{ marginTop: 16, marginBottom: 8 }}>
            <Card>
                <InlineStack align="space-between" blockAlign="center" gap="400" wrap>
                <BlockStack gap="100">
                    <Text as="h3" variant="headingMd">Loving the HW Currency Converter?</Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                        Tap a star to rate us — it takes a second and helps us a lot.
                    </Text>
                </BlockStack>
                <div style={{ display: "flex", gap: 4 }} onMouseLeave={() => setHover(0)}>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            type="button"
                            onMouseEnter={() => setHover(n)}
                            onClick={() => onStar(n)}
                            aria-label={`${n} star${n > 1 ? "s" : ""}`}
                            style={{
                                background: "none",
                                border: 0,
                                padding: 2,
                                cursor: "pointer",
                                lineHeight: 0,
                                transform: n === hover ? "scale(1.15)" : "scale(1)",
                                transition: "transform .12s ease",
                            }}
                        >
                            <svg
                                width="34"
                                height="34"
                                viewBox="0 0 24 24"
                                fill={n <= active ? "#ffb800" : "#e4e5e7"}
                                stroke={n <= active ? "#ffb800" : "#c9cccf"}
                                strokeWidth="1.2"
                            >
                                <path d={STAR_PATH} />
                            </svg>
                        </button>
                    ))}
                </div>
                </InlineStack>
            </Card>
        </div>
    );
}

export default ReviewBanner;
